import { Box, FormControlLabel, Tooltip } from '@mui/material';
import {
  ApplicationFieldName,
  parsePublicApplicationError,
  submitApplicationRequest,
} from 'api/requests/public-application-requests';
import UPButton from 'components/button';
import ErrorBar from 'components/error-bar';
import UPInput from 'components/input';
import { UPInputLabel } from 'components/input/styles';
import UPSwitch from 'components/switch';
import i18next from 'i18next';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  GENERAL__EMAIL,
  GENERAL__ERROR_MESSAGE,
  GENERAL__FIRST_NAME,
  GENERAL__HERE,
  GENERAL__LAST_NAME,
  JOB_APPLICANTS__CV_TOO_LARGE,
  JOB_APPLICANTS__CV_TYPE_NOT_ALLOWED,
  JOB_APPLICANTS__DUPLICATE_APPLICATION,
  JOB_APPLICANTS__LINK_NOT_ACTIVE,
  JOB_APPLICANTS__PRIVACY_STATEMENT_REQUIRED,
  JOB_APPLICATION__FORM_INTRO,
  JOB_APPLICATION__FORM_TITLE,
  JOB_APPLICATION__MOTIVATION,
  JOB_APPLICATION__MOTIVATION_PLACEHOLDER,
  JOB_APPLICATION__PHONE,
  JOB_APPLICATION__PRIVACY_CONFIRM,
  JOB_APPLICATION__PRIVACY_REQUIRED_ERROR,
  JOB_APPLICATION__PRIVACY_TOOLTIP,
  JOB_APPLICATION__REQUEST_TOO_LARGE,
  JOB_APPLICATION__SUBMIT,
} from 'translations/constants';
import { getInputErrorText } from 'utils/helpers';
import ApplicationSubmitted from './ApplicationSubmitted';
import CvUpload from './CvUpload';
import {
  CharacterCounter,
  FieldErrorText,
  FieldRow,
  FormCard,
  MotivationTextArea,
  PageIntro,
  PageTitle,
} from './styles';
import {
  CvRejectionReason,
  EMAIL_MAX_LENGTH,
  EMAIL_PATTERN,
  MOTIVATION_MAX_LENGTH,
  NAME_MAX_LENGTH,
  PHONE_MAX_LENGTH,
  PHONE_PATTERN,
  requiredNonBlank,
  toLanguageId,
} from './validation';

export interface ApplicationFormProps {
  token: string;
  /** Null is normal — the validity response may omit it on an otherwise valid link. */
  companyName: string | null;
  /** Called when the server reports the link died while the form was open. */
  onLinkRevoked: () => void;
}

export interface ApplicationFormValues {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  motivationalText: string;
  privacyAccepted: boolean;
}

const EMPTY_FORM: ApplicationFormValues = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  motivationalText: '',
  privacyAccepted: false,
};

const PRIVACY_STATEMENT_URL = 'https://www.update-pro.be/privacyverklaring/';

const ApplicationForm = (props: ApplicationFormProps): JSX.Element => {
  const { token, companyName, onLinkRevoked } = props;
  const [t] = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [bannerKey, setBannerKey] = useState<string | null>(null);
  const [cv, setCv] = useState<File | null>(null);
  const [cvServerRejection, setCvServerRejection] = useState<CvRejectionReason | null>(null);

  const {
    control,
    handleSubmit,
    setError,
    watch,
    formState: { errors },
  } = useForm<ApplicationFormValues>({ defaultValues: EMPTY_FORM, mode: 'onSubmit' });

  const motivationLength = watch('motivationalText').length;
  const hasConsented = watch('privacyAccepted');

  const applyFieldErrors = (fields: ApplicationFieldName[]) => {
    fields.forEach(fieldName => {
      if (fieldName === 'privacyStatementAccepted') {
        setError('privacyAccepted', { type: 'required' });
        return;
      }
      // `pattern`, not `required`: the value was sent, the server refused its shape.
      setError(fieldName, { type: 'pattern' });
    });
  };

  const handleFailure = (error: unknown) => {
    const failure = parsePublicApplicationError(error);

    if (failure.kind === 'tooLarge') {
      // No server key exists: Kestrel rejects the body before application code runs.
      setBannerKey(JOB_APPLICATION__REQUEST_TOO_LARGE);
      return;
    }

    if (failure.kind === 'fieldErrors') {
      applyFieldErrors(failure.fields);
      return;
    }

    if (failure.kind === 'errorKey') {
      if (failure.key === JOB_APPLICANTS__LINK_NOT_ACTIVE) {
        // Revoked while the form was open — hand the page back to the dead-link state.
        onLinkRevoked();
        return;
      }
      if (failure.key === JOB_APPLICANTS__CV_TYPE_NOT_ALLOWED) {
        setCvServerRejection('type');
        return;
      }
      if (failure.key === JOB_APPLICANTS__CV_TOO_LARGE) {
        setCvServerRejection('size');
        return;
      }
      if (failure.key === JOB_APPLICANTS__PRIVACY_STATEMENT_REQUIRED) {
        setError('privacyAccepted', { type: 'required' });
        return;
      }
      if (failure.key === JOB_APPLICANTS__DUPLICATE_APPLICATION) {
        // Blocking; the way forward is a different address, so flag the field too.
        setError('email', { type: 'validate' });
        setBannerKey(JOB_APPLICANTS__DUPLICATE_APPLICATION);
        return;
      }
      // Any other key still translates: `message` is null for anonymous callers, `error` is not.
      setBannerKey(failure.key);
      return;
    }

    setBannerKey(GENERAL__ERROR_MESSAGE);
  };

  const onSubmit = async (values: ApplicationFormValues) => {
    setIsSubmitting(true);
    setBannerKey(null);
    setCvServerRejection(null);
    try {
      await submitApplicationRequest(token, {
        firstName: values.firstName.trim(),
        lastName: values.lastName.trim(),
        email: values.email.trim(),
        phone: values.phone.trim(),
        motivationalText: values.motivationalText.trim(),
        privacyStatementAccepted: values.privacyAccepted,
        languageId: toLanguageId(i18next.language),
        cv,
      });
      setIsSubmitted(true);
    } catch (error) {
      handleFailure(error);
    } finally {
      // Always re-enabled: nothing is written on any error, so a retry cannot double-submit.
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return <ApplicationSubmitted />;
  }

  return (
    <FormCard>
      {bannerKey && <ErrorBar message={t(bannerKey)} />}
      <PageTitle>{t(JOB_APPLICATION__FORM_TITLE)}</PageTitle>
      <PageIntro>
        {companyName ? `${t(JOB_APPLICATION__FORM_INTRO)} ${companyName}` : t(JOB_APPLICATION__FORM_INTRO)}
      </PageIntro>

      <FieldRow>
        <Controller
          name="firstName"
          control={control}
          rules={{ required: true, maxLength: NAME_MAX_LENGTH, validate: requiredNonBlank }}
          render={({ field }) => (
            <UPInput
              value={field.value}
              onChange={field.onChange}
              label={t(GENERAL__FIRST_NAME)}
              placeholder={t(GENERAL__FIRST_NAME)}
              required
              fullWidth
              error={!!errors.firstName}
              helperText={getInputErrorText(errors, 'firstName', undefined, NAME_MAX_LENGTH)}
            />
          )}
        />
      </FieldRow>

      <FieldRow>
        <Controller
          name="lastName"
          control={control}
          rules={{ required: true, maxLength: NAME_MAX_LENGTH, validate: requiredNonBlank }}
          render={({ field }) => (
            <UPInput
              value={field.value}
              onChange={field.onChange}
              label={t(GENERAL__LAST_NAME)}
              placeholder={t(GENERAL__LAST_NAME)}
              required
              fullWidth
              error={!!errors.lastName}
              helperText={getInputErrorText(errors, 'lastName', undefined, NAME_MAX_LENGTH)}
            />
          )}
        />
      </FieldRow>

      <FieldRow>
        <Controller
          name="email"
          control={control}
          rules={{ required: true, maxLength: EMAIL_MAX_LENGTH, pattern: EMAIL_PATTERN }}
          render={({ field }) => (
            <UPInput
              value={field.value}
              onChange={field.onChange}
              label={t(GENERAL__EMAIL)}
              placeholder={t(GENERAL__EMAIL)}
              required
              fullWidth
              autoComplete="off"
              error={!!errors.email}
              helperText={getInputErrorText(errors, 'email', undefined, EMAIL_MAX_LENGTH, !!errors.email)}
            />
          )}
        />
      </FieldRow>

      <FieldRow>
        <Controller
          name="phone"
          control={control}
          rules={{ required: true, maxLength: PHONE_MAX_LENGTH, pattern: PHONE_PATTERN }}
          render={({ field }) => (
            <UPInput
              value={field.value}
              onChange={field.onChange}
              label={t(JOB_APPLICATION__PHONE)}
              placeholder={t(JOB_APPLICATION__PHONE)}
              required
              fullWidth
              error={!!errors.phone}
              helperText={getInputErrorText(errors, 'phone', undefined, PHONE_MAX_LENGTH, !!errors.phone)}
            />
          )}
        />
      </FieldRow>

      <FieldRow>
        {/* Label sits above the box like UPInput's own, so this field lines up with the others. */}
        <UPInputLabel>{`${t(JOB_APPLICATION__MOTIVATION)} *`}</UPInputLabel>
        <Controller
          name="motivationalText"
          control={control}
          rules={{ required: true, maxLength: MOTIVATION_MAX_LENGTH, validate: requiredNonBlank }}
          render={({ field }) => (
            <MotivationTextArea
              value={field.value}
              onChange={field.onChange}
              aria-label={t(JOB_APPLICATION__MOTIVATION)}
              placeholder={t(JOB_APPLICATION__MOTIVATION_PLACEHOLDER)}
              minRows={5}
              // Capped on the control too, so a paste cannot overshoot the counter. 2000 is valid.
              maxLength={MOTIVATION_MAX_LENGTH}
              hasError={!!errors.motivationalText}
            />
          )}
        />
        <CharacterCounter isAtLimit={motivationLength >= MOTIVATION_MAX_LENGTH}>
          {`${motivationLength}/${MOTIVATION_MAX_LENGTH}`}
        </CharacterCounter>
        {!!errors.motivationalText && (
          <FieldErrorText>
            {getInputErrorText(errors, 'motivationalText', undefined, MOTIVATION_MAX_LENGTH)}
          </FieldErrorText>
        )}
      </FieldRow>

      <CvUpload
        file={cv}
        onFileChange={selected => {
          setCv(selected);
          // A new pick invalidates the previous server verdict.
          setCvServerRejection(null);
        }}
        serverRejection={cvServerRejection}
      />

      <Box>
        <Controller
          name="privacyAccepted"
          control={control}
          rules={{ required: true }}
          render={({ field }) => (
            <FormControlLabel
              control={<UPSwitch checked={field.value} onChange={field.onChange} />}
              // Same shape as the employee modal's GDPR consent. Link colour is inline because it
              // has to read against the dark tooltip.
              label={
                <Tooltip
                  title={
                    <>
                      {`${t(JOB_APPLICATION__PRIVACY_TOOLTIP)} `}
                      <a
                        href={PRIVACY_STATEMENT_URL}
                        target="_blank"
                        rel="noreferrer"
                        style={{ fontStyle: 'italic', textDecoration: 'underline', color: '#DBEDE3' }}
                      >
                        {t(GENERAL__HERE)}
                      </a>
                    </>
                  }
                >
                  <span>{t(JOB_APPLICATION__PRIVACY_CONFIRM)}</span>
                </Tooltip>
              }
              sx={{
                '.MuiTypography-root': { fontSize: '14px', marginLeft: '10px' },
                '&.MuiFormControlLabel-root': {
                  marginTop: '8px',
                  marginRight: '0px',
                  // Cancels MUI's -11px, which absorbs a ripple padding UPSwitchStyled does not
                  // have and would push the toggle out of line with the inputs.
                  marginLeft: '0px',
                },
              }}
            />
          )}
        />
        {!!errors.privacyAccepted && <FieldErrorText>{t(JOB_APPLICATION__PRIVACY_REQUIRED_ERROR)}</FieldErrorText>}
      </Box>

      {/* Disabled until consent, per the US: the toggle gates the action itself, not just
          validation. The extra top margin leaves room for the consent tooltip, which opens
          downwards and would otherwise cover this button — it has to clear the full tooltip,
          not just its arrow. */}
      <Box sx={{ marginTop: '40px' }}>
        <UPButton
          text={t(JOB_APPLICATION__SUBMIT)}
          onClick={() => handleSubmit(onSubmit)()}
          disabled={!hasConsented || isSubmitting}
        />
      </Box>
    </FormCard>
  );
};

export default ApplicationForm;
